import ExcelJS from "exceljs";
import ColorLogo from "../assets/login/ColorLogo.png";
import logo1 from "../assets/header/HABD.png";
import logo2 from "../assets/login/DMW_Patiala.png";

const DownloadSiteDataExcel = async (selectedSites, trainData, fromDate, toDate, setLoading, triggerNotification) => {
  try {
    setLoading(true);

    if (!selectedSites.length || !trainData.length) {
      throw new Error("No sites or train data selected for export");
    }

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Train Overview");

    try {
      // Fetch images
      const colorLogoBuffer = await fetch(ColorLogo).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch ColorLogo.png");
        return res.arrayBuffer();
      });
      const logo1Buffer = await fetch(logo1).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch HABD.png");
        return res.arrayBuffer();
      });
      const logo2Buffer = await fetch(logo2).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch DMW_Patiala.png");
        return res.arrayBuffer();
      });

      // Add images to workbook
      const colorLogoId = workbook.addImage({
        buffer: colorLogoBuffer,
        extension: "png",
      });
      const logo1Id = workbook.addImage({
        buffer: logo1Buffer,
        extension: "png",
      });
      const logo2Id = workbook.addImage({
        buffer: logo2Buffer,
        extension: "png",
      });

      // Merge cells for logos and title (adjusted for 13-column table)
      worksheet.mergeCells("A1:B1"); // ColorLogo area
      worksheet.mergeCells("C1:K1"); // Logo1 + Title area
      worksheet.mergeCells("L1:M1"); // Logo2 area

      // Add ColorLogo centered in A1:B1
      worksheet.addImage(colorLogoId, {
        tl: { col: 0.5, row: 0.5 },
        ext: { width: 120, height: 60 },
      });
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      // Add Logo1 with slight offset in C1:K1 to allow gap before title
      worksheet.addImage(logo1Id, {
        tl: { col: 2.2, row: 0.5 }, // Slight left offset inside merged range
        ext: { width: 60, height: 60 },
      });

      // Add title with proper spacing after logo1
      const title = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("C1").value = title;
      worksheet.getCell("C1").font = {
        bold: true,
        size: 14,
        name: "Helvetica",
      };
      worksheet.getCell("C1").alignment = {
        horizontal: "left", // Left align so it appears after logo
        vertical: "middle",
        indent: 8, // Adds spacing between logo and text
      };

      // Add Logo2 centered in L1:M1
      worksheet.addImage(logo2Id, {
        tl: { col: 11.5, row: 0.5 },
        ext: { width: 80, height: 60 },
      });
      worksheet.getCell("L1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      // Set height for header row
      worksheet.getRow(1).height = 65;

      // console.log("All logos added successfully to header");
    } catch (error) {
      console.error("Error loading logo images:", error);
      
      // Fallback layout without logos
      worksheet.mergeCells("A1:B1");
      worksheet.mergeCells("C1:K1");
      worksheet.mergeCells("L1:M1");

      worksheet.getCell("A1").value = "NOVIUS";
      worksheet.getCell("A1").font = { bold: true, size: 14, color: { argb: "FF0066CC" } };
      worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };

      worksheet.getCell("C1").value = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("C1").font = { bold: true, size: 14, name: "Helvetica" };
      worksheet.getCell("C1").alignment = { horizontal: "center", vertical: "middle" };

      worksheet.getCell("L1").value = "DMW Patiala";
      worksheet.getCell("L1").font = { bold: true, size: 12 };
      worksheet.getCell("L1").alignment = { horizontal: "center", vertical: "middle" };

      worksheet.getRow(1).height = 65;
    }

    // Format dates to "DD Mon YYYY"
    const formatDate = (dateStr) => {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };
    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);

    // Footer: "Downloaded by" and "Download Time" (Row 2)
    const userName = JSON.parse(localStorage.getItem("user"))?.username || "Guest";
    const downloadDate = new Date();
    const formattedDateTime = `${downloadDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })} ${downloadDate.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })} IST`;

    // Add "Downloaded by" on the left (A2)
    worksheet.getCell("A2").value = `Downloaded by: ${userName}`;
    worksheet.getCell("A2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("A2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    // Add "Download Time" on the right (M2)
    worksheet.getCell("M2").value = `Download Time: ${formattedDateTime}`;
    worksheet.getCell("M2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("M2").alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    worksheet.getRow(2).height = 20;

    // Add Sites and Date Range (Row 3 and 4)
    const siteRow = worksheet.addRow([]);
    siteRow.getCell(1).value = "Sites:";
    siteRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    siteRow.getCell(2).value = selectedSites.join(", ");
    siteRow.getCell(2).font = { size: 11, name: "Calibri" };

    const dateRow = worksheet.addRow([]);
    dateRow.getCell(1).value = "Date Range:";
    dateRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    dateRow.getCell(2).value = `${formattedFromDate} to ${formattedToDate}`;
    dateRow.getCell(2).font = { size: 11, name: "Calibri" };

    worksheet.addRow([]); // Spacer

    const overviewRow = worksheet.addRow([]);
    overviewRow.getCell(1).value = "Train Overview";
    overviewRow.getCell(1).font = { bold: true, size: 14, name: "Calibri" };

    worksheet.addRow([]); // Spacer

    // Table headers
    const headers = [
      "Sr. No.",
      "Status",
      "Site Name",
      "Date & Time",
      "Direction",
      "Train Type",
      "Max Axle Temp (°C)",
      "Avg Axle Temp (°C)",
      "Ambient Temp (°C)",
      "Max Wheel Temp (°C)",
      "Avg Wheel Temp (°C)",
      "Axles",
      "Speed (km/h)",
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, name: "Helvetica" };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "808080" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    headerRow.height = 25;

    // Clean HTML from dateTime
    const cleanDateTime = (dateTime) => {
      if (!dateTime || dateTime === "N/A") return "N/A";
      return dateTime.replace(/<[^>]+>/g, " ").replace(/\s+\|\s+/, " ").trim();
    };

    // Add train data
    trainData.forEach((train, index) => {
      const row = worksheet.addRow([
        index + 1,
        train.status.charAt(0).toUpperCase() + train.status.slice(1),
        train.siteName || "N/A",
        cleanDateTime(train.dateTime),
        train.direction || "N/A",
        train.trainType || "N/A",
        train.maxAxleTemp !== "N/A" ? `${train.maxAxleTemp} °C` : "N/A",
        train.avgAxleTemp !== "N/A" ? `${train.avgAxleTemp} °C` : "N/A",
        train.ambientTemp !== "N/A" ? `${train.ambientTemp} °C` : "N/A",
        train.maxWheelTemp !== "N/A" ? `${train.maxWheelTemp} °C` : "N/A",
        train.avgWheelTemp !== "N/A" ? `${train.avgWheelTemp} °C` : "N/A",
        train.axles || "N/A",
        train.speed !== "N/A" ? `${train.speed} km/h` : "N/A",
      ]);
      
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        
        // Highlight status column based on value
        if (colNumber === 2) { // Status column
          const status = cell.value?.toString().toLowerCase();
          if (status === "alarm") {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF0000" }, // Red background
            };
            cell.font = { color: { argb: "FFFFFF" } }; // White text
          } else if (status === "warning") {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFF00" }, // Yellow background
            };
          }
        }
      });
    });

    // Set column widths based on content
    worksheet.columns = [
      { width: 8 },   // Sr. No.
      { width: 12 },  // Status
      { width: 25 },  // Site Name (wider for better readability)
      { width: 20 },  // Date & Time
      { width: 12 },  // Direction
      { width: 15 },  // Train Type
      { width: 18 },  // Max Axle Temp
      { width: 18 },  // Avg Axle Temp
      { width: 18 },  // Ambient Temp
      { width: 18 },  // Max Wheel Temp
      { width: 18 },  // Avg Wheel Temp
      { width: 10 },  // Axles
      { width: 15 },  // Speed
    ];

    // Generate and download Excel file
    const excelFileName = `Site_Overview_${selectedSites.join("_")}_${fromDate}_to_${toDate}.xlsx`;
    
    // console.log("Generating Excel buffer...");
    const buffer = await workbook.xlsx.writeBuffer();
    // console.log("Excel buffer generated, size:", buffer.byteLength, "bytes");
    
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = excelFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // console.log("Excel file downloaded successfully");
    triggerNotification("Excel generated successfully", "success");
  } catch (error) {
    console.error("Error generating Excel:", error);
    triggerNotification(`Failed to generate Excel: ${error.message}`, "error");
  } finally {
    setLoading(false);
  }
};

export default DownloadSiteDataExcel;